import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Pencil, Download, Link2, Clock } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, Loader, EmptyState } from "@/components/common/Loader";
import { Field, TextInput } from "@/components/common/Forms";
import { FileThumb, FilePicker, FilePreviewDialog } from "@/components/common/Files";
import { api, errMsg, useFetch } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";

function DownloadsDialog({ post, onClose }) {
  const { data, loading, error } = useFetch(`/admin/social/${post.id}/downloads`);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-ivory-card rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="social-downloads-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-normal">Chi l'ha scaricato</DialogTitle>
          <DialogDescription>{post.title}</DialogDescription>
        </DialogHeader>
        {loading ? <Loader /> : error || !data ? <p className="text-sm text-destructive">{error || "Dati non disponibili"}</p> : (
          <>
            <p data-testid="social-downloads-summary" className="text-sm">
              <strong>{data.centers.length}</strong> {data.centers.length === 1 ? "centro" : "centri"} su {data.approved_centers} che possono vederlo
            </p>
            {data.centers.length === 0 ? <p className="text-sm text-warm-text">Nessun centro ha ancora scaricato questo contenuto.</p> : (
              <ul className="divide-y divide-warm/30">
                {data.centers.map((c) => (
                  <li key={c.center_id} data-testid={`social-download-row-${c.center_id}`} className="py-3">
                    <div className="flex justify-between gap-3">
                      <Link to={`/admin/centri/${c.center_id}`} className="text-sm underline underline-offset-4 decoration-warm">{c.center_name}</Link>
                      <span className="text-xs text-warm-text whitespace-nowrap">{c.count} download</span>
                    </div>
                    <p className="text-xs text-warm-text mt-1">Ultimo: {fmtDateTime(c.last_at)} · {c.files.join(", ")}</p>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const ACCEPT = ".png,.jpg,.jpeg,.webp,.mp4,.mov,.webm,.m4v";

function toLocalInput(isoStr) {
  const d = new Date(isoStr);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function ShareLink({ p }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(p.share_url);
      toast.success("Link copiato: incollalo nel gruppo Telegram");
    } catch (e) {
      window.prompt("Copia il link:", p.share_url);
    }
  };
  return (
    <button data-testid={`admin-social-share-${p.id}`} onClick={copy} className="btn-link inline-flex items-center gap-1.5 mt-2 ml-4">
      <Link2 className="w-3.5 h-3.5" /> Copia link per Telegram
    </button>
  );
}

function PostDialog({ open, onOpenChange, post, onDone }) {
  const editing = !!post;
  const [title, setTitle] = useState(post?.title || "");
  const [caption, setCaption] = useState(post?.caption || "");
  const [partner, setPartner] = useState(post?.partner_only || false);
  const [when, setWhen] = useState(post?.scheduled ? toLocalInput(post.published_at) : "");
  const [postFiles, setPostFiles] = useState([]);
  const [storyFiles, setStoryFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim()) return toast.error("Inserisci un titolo");
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/admin/social/${post.id}`, { title, caption, partner_only: partner, ...(post.scheduled ? { publish_at: when ? new Date(when).toISOString() : "" } : {}) });
        for (const [kind, files] of [["post", postFiles], ["story", storyFiles]]) {
          if (!files.length) continue;
          const fd = new FormData();
          fd.append("kind", kind);
          files.forEach((f) => fd.append("files", f));
          await api.post(`/admin/social/${post.id}/files`, fd);
        }
        toast.success("Contenuto aggiornato");
      } else {
        if (!postFiles.length && !storyFiles.length) {
          setBusy(false);
          return toast.error("Carica almeno un file per il post o per la storia");
        }
        const fd = new FormData();
        fd.append("title", title);
        fd.append("caption", caption);
        fd.append("partner_only", partner ? "true" : "false");
        if (when) fd.append("publish_at", new Date(when).toISOString());
        postFiles.forEach((f) => fd.append("post_files", f));
        storyFiles.forEach((f) => fd.append("story_files", f));
        await api.post("/admin/social", fd);
        toast.success(when && new Date(when) > new Date() ? "Contenuto programmato. I centri riceveranno l'email quando andrà online." : "Contenuto pubblicato. I centri riceveranno un'email.");
      }
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-ivory-card rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="social-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-normal">{editing ? "Modifica contenuto" : "Nuovo contenuto social"}</DialogTitle>
          <DialogDescription>Immagini (PNG, JPG, WEBP) o video (MP4, MOV, WEBM) fino a 200 MB ciascuno.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <Field label="Titolo *"><TextInput data-testid="social-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Es. Settimana del viso – ottobre" /></Field>
          <Field label="File per il post"><FilePicker files={postFiles} setFiles={setPostFiles} accept={ACCEPT} testid="social-post-files" label={editing ? "Aggiungi file al post" : "Scegli i file del post"} /></Field>
          <Field label="File per la storia"><FilePicker files={storyFiles} setFiles={setStoryFiles} accept={ACCEPT} testid="social-story-files" label={editing ? "Aggiungi file alla storia" : "Scegli i file della storia"} /></Field>
          <Field label="Caption">
            <Textarea data-testid="social-caption-input" value={caption} onChange={(e) => setCaption(e.target.value)} rows={6} className="bg-ivory-deep rounded-2xl" placeholder="Testo e hashtag da copiare" />
          </Field>
          <label className="flex items-center justify-between gap-4 bg-ivory-deep border border-warm/50 px-4 py-3 text-sm">
            <span>Solo per Centri Official Partner<span className="block text-xs text-warm-text">Gli altri centri lo vedranno oscurato</span></span>
            <Switch data-testid="social-partner-switch" checked={partner} onCheckedChange={setPartner} />
          </label>
          {(!editing || post.scheduled) && (
            <Field label="Programma la pubblicazione">
              <TextInput data-testid="social-schedule-input" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
              <p className="text-xs text-warm-text mt-2">{when ? "Il contenuto e l'email ai centri partiranno a quest'ora." : "Lascia vuoto per pubblicarlo subito."}</p>
            </Field>
          )}
          <button data-testid="social-submit-btn" disabled={busy} onClick={submit} className="btn-gold w-full">{busy ? "Caricamento…" : editing ? "Salva" : when ? "Programma" : "Pubblica"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AdminPost({ p, onEdit, onChanged, onOpen, onStats }) {
  const delFile = async (f) => {
    if (!window.confirm(`Eliminare ${f.filename}?`)) return;
    try {
      await api.delete(`/admin/social/files/${f.id}`);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const delPost = async () => {
    if (!window.confirm(`Eliminare il contenuto "${p.title}"? I centri non lo vedranno più.`)) return;
    try {
      await api.delete(`/admin/social/${p.id}`);
      toast.success("Contenuto eliminato");
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <article data-testid={`admin-social-post-${p.id}`} className="lux-card p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap"><p className="eyebrow">{fmtDate(p.published_at)}</p>{p.scheduled && <span data-testid={`admin-social-scheduled-${p.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-gold/10 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.16em] text-gold"><Clock className="w-3 h-3" /> Programmato · {fmtDateTime(p.published_at)}</span>}{p.partner_only && <span className="text-[10px] uppercase tracking-[0.18em] text-gold">Solo Official Partner</span>}</div>
          <h2 className="text-2xl font-normal mt-1">{p.title}</h2>
          <p className="text-xs text-warm-text mt-1">{p.scheduled ? "Programmato" : "Pubblicato"} da {p.created_by_name}</p>
          <button data-testid={`admin-social-stats-${p.id}`} onClick={() => onStats(p)} className="btn-link inline-flex items-center gap-1.5 mt-2">
            <Download className="w-3.5 h-3.5" /> Scaricato da {p.download_centers} {p.download_centers === 1 ? "centro" : "centri"}
          </button>
          <ShareLink p={p} />
        </div>
        <div className="flex gap-4">
          <button data-testid={`admin-social-edit-${p.id}`} onClick={() => onEdit(p)} aria-label="Modifica"><Pencil className="w-4 h-4" strokeWidth={1.4} /></button>
          <button data-testid={`admin-social-delete-${p.id}`} onClick={delPost} aria-label="Elimina"><Trash2 className="w-4 h-4 text-destructive" strokeWidth={1.4} /></button>
        </div>
      </div>
      <div className="flex gap-3 overflow-x-auto mt-5">
        {p.files.map((f) => (
          <div key={f.id} className="shrink-0 w-28">
            <button onClick={() => onOpen(f)} className="block w-full"><FileThumb kind="social" file={f} className={`w-full ${f.kind === "story" ? "aspect-[9/16]" : "aspect-[4/5]"}`} /></button>
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-[10px] uppercase tracking-[0.14em] text-warm-text">{f.kind === "story" ? "Storia" : "Post"}</span>
              <button data-testid={`admin-social-file-delete-${f.id}`} onClick={() => delFile(f)} aria-label="Elimina file"><Trash2 className="w-3 h-3 text-destructive" /></button>
            </div>
          </div>
        ))}
      </div>
      {p.caption && <p className="text-sm text-warm-text mt-4 whitespace-pre-wrap line-clamp-3">{p.caption}</p>}
    </article>
  );
}

export default function AdminSocial() {
  const { data, loading, reload } = useFetch("/admin/social");
  const [dialog, setDialog] = useState(null);
  const [file, setFile] = useState(null);
  const [stats, setStats] = useState(null);
  return (
    <div>
      <PageHeader eyebrow="Contenuti social" title="Grafiche e video per i centri" subtitle="Carica i file per post e storie con la caption: i centri li scaricano pronti da pubblicare."
        actions={<button data-testid="new-social-btn" onClick={() => setDialog({ post: null })} className="btn-gold"><Plus className="w-4 h-4" /> Nuovo contenuto</button>} />
      {loading && !data ? <Loader /> : !data?.length ? <EmptyState title="Nessun contenuto" text="Pubblica il primo contenuto: i centri riceveranno un'email." /> : (
        <div className="space-y-4">
          {data.map((p) => <AdminPost key={p.id} p={p} onEdit={(post) => setDialog({ post })} onChanged={reload} onOpen={setFile} onStats={setStats} />)}
        </div>
      )}
      {dialog && <PostDialog key={dialog.post?.id || "new"} open onOpenChange={(o) => !o && setDialog(null)} post={dialog.post} onDone={reload} />}
      {stats && <DownloadsDialog post={stats} onClose={() => setStats(null)} />}
      <FilePreviewDialog kind="social" file={file} onClose={() => setFile(null)} />
    </div>
  );
}
